import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { LuSave, LuPlus } from 'react-icons/lu';

import { FormSelectField, Button, TableLookup } from '_components/_core';
import { SPECIAL_COLUMNS } from '_components/_core/Table';

import ValidationSchema from './utilities/ValidationSchema';

import {
  CustomCard,
  CustomCardBody,
  CustomCardFooter,
  CustomCardHeader,
} from '../../../../styles';

const tableConfig = {
  defaultFilters: {},
  defaultSorting: { field: 'name', order: 'asc' },
  defaultPagination: { currentPage: 1, itemsPerPage: 10 },
};

const PaymentMethodGateway = ({
  onCreatePaymentMethodGateway,
  onUpdatePaymentMethodGateway,
  onDeletePaymentMethodGateway,
  onFetchPaymentMethodGateway,
  onFetchPaymentMethods,
  onFetchPaymentGateways,
  paymentMethodGateways,
  paymentMethods,
  paymentGateways,
  isLoading,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentAssociation, setCurrentAssociation] = useState(null);
  const [filteredMethodsGateways, setFilteredMethodsGateways] = useState([]);

  useEffect(() => {
    onFetchPaymentMethodGateway();
    onFetchPaymentMethods();
    onFetchPaymentGateways();
  }, [onFetchPaymentMethodGateway, onFetchPaymentMethods, onFetchPaymentGateways]);

  const handleCreateAssociation = (values, { resetForm }) => {
    if (isEditing && currentAssociation) {
      onUpdatePaymentMethodGateway(currentAssociation.id, values);
      setIsEditing(false);
      setCurrentAssociation(null);
    } else {
      onCreatePaymentMethodGateway(values);
    }
    resetForm();
  };

  const handleEditAssociation = association => {
    const selectedAssociation = paymentMethodGateways.find(
      methodGateway => methodGateway.id === association,
    );

    setCurrentAssociation({
      ...selectedAssociation,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = resetForm => {
    setIsEditing(false);
    setCurrentAssociation(null);
    resetForm();
  };

  const handleConfirmDelete = association => {
    onDeletePaymentMethodGateway(association, () => {
      onFetchPaymentMethodGateway();
    });
  };

  useEffect(() => {
    if (
      paymentMethodGateways?.length &&
      paymentMethods?.length &&
      paymentGateways?.length
    ) {
      const formattedData = paymentMethodGateways.map(association => ({
        ...association,
        payment_method_name: getMethodNameById(association.payment_method_id),
        payment_gateway_name: getGatewayNameById(association.payment_gateway_id),
      }));
      setFilteredMethodsGateways(formattedData);
    }
  }, [paymentMethodGateways, paymentMethods, paymentGateways]);

  // Format options for select fields
  const paymentMethodOptions =
    paymentMethods?.map(method => ({
      value: method.id,
      label: method.name,
    })) || [];

  const paymentGatewayOptions =
    paymentGateways?.map(gateway => ({
      value: gateway.id,
      label: gateway.name,
    })) || [];

  // Helper function to get method or gateway name by id
  const getMethodNameById = id => {
    const method = paymentMethods?.find(m => m.id === id);
    return method ? method.name : 'Desconhecido';
  };

  const getGatewayNameById = id => {
    const gateway = paymentGateways?.find(g => g.id === id);
    return gateway ? gateway.name : 'Desconhecido';
  };

  const columns = [
    {
      label: 'Gateway de Pagamento',
      keyName: 'payment_gateway_name',
    },
    {
      label: 'Método de Pagamento',
      keyName: 'payment_method_name',
    },
    {
      label: 'Ações',
      keyName: 'id',
      specialType: SPECIAL_COLUMNS.ACTIONS,
      align: 'center',
    },
  ];

  return (
    <>
      <Formik
        initialValues={{
          payment_method_id: currentAssociation?.payment_method_id || '',
          payment_gateway_id: currentAssociation?.payment_gateway_id || '',
        }}
        enableReinitialize
        validationSchema={ValidationSchema}
        onSubmit={handleCreateAssociation}
      >
        {({ handleSubmit, resetForm }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <CustomCard noBorders>
                  <CustomCardHeader>
                    <h3>{isEditing ? 'Editar Associação' : 'Adicionar Associação'}</h3>
                  </CustomCardHeader>
                  <CustomCardBody>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">
                            Gateway de Pagamento
                          </Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormSelectField
                            name="payment_gateway_id"
                            options={paymentGatewayOptions}
                            placeholder="Selecione um gateway de pagamento"
                            isSearchable={true}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">
                            Método de Pagamento
                          </Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormSelectField
                            name="payment_method_id"
                            options={paymentMethodOptions}
                            placeholder="Selecione um método de pagamento"
                            isSearchable={true}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                  </CustomCardBody>
                  <CustomCardFooter className="d-flex justify-content-center align-items-center">
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      disabled={isLoading}
                      className="d-flex align-items-center"
                    >
                      {isEditing ? 'Atualizar' : 'Adicionar'}
                      {isEditing ? (
                        <LuSave className="ml-2" />
                      ) : (
                        <LuPlus className="ml-2" />
                      )}
                    </Button>
                    <Button
                      variant="link"
                      type="button"
                      size="sm"
                      disabled={isLoading}
                      onClick={() => handleCancelEdit(resetForm)}
                    >
                      Cancelar
                    </Button>
                  </CustomCardFooter>
                </CustomCard>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Row className="mt-4">
        <Col>
          <TableLookup
            keyName="id"
            name="payment-gateways-lookup-table"
            columns={columns}
            data={filteredMethodsGateways || []}
            isLoading={isLoading}
            onEdit={handleEditAssociation}
            onDelete={handleConfirmDelete}
            {...tableConfig}
          />
        </Col>
      </Row>
    </>
  );
};

PaymentMethodGateway.propTypes = {
  onCreatePaymentMethodGateway: PropTypes.func.isRequired,
  onUpdatePaymentMethodGateway: PropTypes.func.isRequired,
  onDeletePaymentMethodGateway: PropTypes.func.isRequired,
  onFetchPaymentMethodGateway: PropTypes.func.isRequired,
  onFetchPaymentMethods: PropTypes.func.isRequired,
  onFetchPaymentGateways: PropTypes.func.isRequired,
  paymentMethodGateways: PropTypes.array,
  paymentMethods: PropTypes.array,
  paymentGateways: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default PaymentMethodGateway;
